import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  inject,
  forwardRef,
  Injector,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
} from '@angular/forms';
import { FileSelectModule, FileSelectComponent } from '@progress/kendo-angular-upload';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { IconsModule } from '@progress/kendo-angular-icons';
import {
  fileIcon,
  xIcon,
  uploadIcon,
  imageIcon,
  filePdfIcon,
  fileExcelIcon,
} from '@progress/kendo-svg-icons';

// Types and Interfaces
export type FileUploadType = 'image' | 'document' | 'excel' | 'pdf' | 'video' | 'audio' | 'custom';

export interface FileUploadConfig {
  acceptedFileTypes: string[];
  acceptAttribute: string;
  maxFileSize: number; // in MB
  showPreview: boolean;
  selectButtonText: string;
  changeButtonText: string;
  removeButtonText: string;
  placeholderText: string;
  showValidationError: boolean;
  validateDimensions?: boolean;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  customErrorMessage?: string;
  labelText?: string;
}

export interface FileUploadResult {
  file: File | null;
  base64?: string;
  preview?: string;
  error?: string;
  isValid: boolean;
}

export interface FileUploadComponentConfig {
  type?: FileUploadType;
  disabled?: boolean;
  required?: boolean;
  multiple?: boolean;
  cssClass?: string;
  showFileInfo?: boolean;
  allowDragDrop?: boolean;
  customConfig?: Partial<FileUploadConfig>;
  initialFile?: File | null;
}

export const MIME_TYPES = {
  APPLICATION_PDF: 'application/pdf',
  APPLICATION_JSON: 'application/json',
  TEXT_PLAIN: 'text/plain',
  IMAGE_PNG: 'image/png',
  IMAGE_JPEG: 'image/jpeg',
  IMAGE_JPG: 'image/jpg',
} as const;

@Component({
  selector: 'app-file-upload',
  standalone: true,
  imports: [CommonModule, FormsModule, FileSelectModule, ButtonsModule, IconsModule],
  templateUrl: './file-upload.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploadComponent),
      multi: true,
    },
    // ADD THIS PROVIDER:
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => FileUploadComponent),
      multi: true,
    },
  ],
})
export class FileUploadComponent {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly injector = inject(Injector);

  @ViewChild('fileSelect') fileSelect!: FileSelectComponent;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  // Inputs
  @Input() componentConfig?: FileUploadComponentConfig;
  type: FileUploadType = 'custom';
  disabled: boolean = false;
  required: boolean = false;
  multiple: boolean = false;
  cssClass: string = '';
  showFileInfo: boolean = true;
  allowDragDrop: boolean = true;
  customConfig?: Partial<FileUploadConfig>;
  initialFile?: File | null;
  touched: boolean = false;
  errors: ValidationErrors | null = null;
  public ngControl: NgControl | null = null;
  showErrors: boolean = true;

  // Outputs
  @Output() fileSelectEvent = new EventEmitter<FileUploadResult>();
  @Output() fileRemove = new EventEmitter<void>();
  @Output() filesChange = new EventEmitter<FileUploadResult[]>();
  @Output() validationError = new EventEmitter<string>();

  // Component state
  selectedFile: File | null = null;
  selectedFiles: File[] = [];
  preview: string | null = null;
  errorMessage: string = '';
  isLoading: boolean = false;
  isDragOver: boolean = false;
  inputId: string = '';

  // Icons
  uploadIcon = uploadIcon;
  fileIcon = fileIcon;
  xIcon = xIcon;
  imageIcon = imageIcon;
  pdfIcon = filePdfIcon;
  excelIcon = fileExcelIcon;

  // Type configurations
  private readonly typeConfigurations: Record<FileUploadType, FileUploadConfig> = {
    image: {
      acceptedFileTypes: ['image/png', 'image/jpg', 'image/jpeg'],
      acceptAttribute: '.png,.jpg,.jpeg',
      maxFileSize: 5,
      showPreview: true,
      selectButtonText: 'Select image',
      changeButtonText: 'Change',
      removeButtonText: 'Remove',
      placeholderText: 'Upload image',
      showValidationError: true,
      validateDimensions: false,
      customErrorMessage: 'Only PNG, JPG and JPEG images are allowed',
      labelText: '',
    },
    document: {
      acceptedFileTypes: [
        MIME_TYPES.APPLICATION_PDF,
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
      ],
      acceptAttribute: '.pdf,.doc,.docx,.txt',
      maxFileSize: 10,
      showPreview: false,
      selectButtonText: 'Select document',
      changeButtonText: 'Change',
      removeButtonText: 'Remove',
      placeholderText: 'Upload document',
      showValidationError: true,
      customErrorMessage: 'Only PDF, DOC, DOCX, and TXT documents are allowed (max 10MB)',
      labelText: '',
    },
    excel: {
      acceptedFileTypes: [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv',
      ],
      acceptAttribute: '.xls,.xlsx,.csv',
      maxFileSize: 15,
      showPreview: false,
      selectButtonText: 'Select spreadsheet',
      changeButtonText: 'Change',
      removeButtonText: 'Remove',
      placeholderText: 'Upload spreadsheet',
      showValidationError: true,
      customErrorMessage: 'Only XLS, XLSX, and CSV files are allowed (max 15MB)',
      labelText: '',
    },
    pdf: {
      acceptedFileTypes: [MIME_TYPES.APPLICATION_PDF],
      acceptAttribute: '.pdf',
      maxFileSize: 20,
      showPreview: false,
      selectButtonText: 'Select PDF',
      changeButtonText: 'Change',
      removeButtonText: 'Remove',
      placeholderText: 'Upload PDF',
      showValidationError: true,
      customErrorMessage: 'Only PDF files are allowed (max 20MB)',
      labelText: '',
    },
    video: {
      acceptedFileTypes: ['video/mp4', 'video/mpeg', 'video/quicktime', 'video/webm'],
      acceptAttribute: '.mp4,.mpeg,.mov,.webm',
      maxFileSize: 100,
      showPreview: false,
      selectButtonText: 'Select video',
      changeButtonText: 'Change',
      removeButtonText: 'Remove',
      placeholderText: 'Upload video',
      showValidationError: true,
      customErrorMessage: 'Only MP4, MPEG, MOV, and WEBM video files are allowed (max 100MB)',
      labelText: '',
    },
    audio: {
      acceptedFileTypes: ['audio/mpeg', 'audio/wav', 'audio/mp3', 'audio/ogg'],
      acceptAttribute: '.mp3,.wav,.ogg,.mpeg',
      maxFileSize: 50,
      showPreview: false,
      selectButtonText: 'Select audio',
      changeButtonText: 'Change',
      removeButtonText: 'Remove',
      placeholderText: 'Upload audio',
      showValidationError: true,
      customErrorMessage: 'Only MP3, WAV, and OGG audio files are allowed (max 50MB)',
      labelText: '',
    },
    custom: {
      acceptedFileTypes: ['*/*'],
      acceptAttribute: '*',
      maxFileSize: 10,
      showPreview: false,
      selectButtonText: 'Select file',
      changeButtonText: 'Change',
      removeButtonText: 'Remove',
      placeholderText: 'Upload file',
      showValidationError: true,
      customErrorMessage: 'Invalid file',
      labelText: '',
    },
  };

  private onTouchedCallback: () => void = () => {};
  private onChangeCallback: (_: any) => void = () => {};

  ngOnInit(): void {
    // Generate unique ID for the input
    this.inputId = `file-input-${Math.random().toString(36).substr(2, 9)}`;
    this.ngControl = this.injector.get(NgControl, null);
    if (this.componentConfig) {
      this.type = this.componentConfig.type ?? this.type;
      this.disabled = this.componentConfig.disabled ?? this.disabled;
      this.required = this.componentConfig.required ?? this.required;
      this.multiple = this.componentConfig.multiple ?? this.multiple;
      this.cssClass = this.componentConfig.cssClass ?? this.cssClass;
      this.showFileInfo = this.componentConfig.showFileInfo ?? this.showFileInfo;
      this.allowDragDrop = this.componentConfig.allowDragDrop ?? this.allowDragDrop;
      this.customConfig = this.componentConfig.customConfig ?? this.customConfig;
      this.initialFile = this.componentConfig.initialFile ?? this.initialFile;
    }
    if (this.initialFile) {
      this.handleFileSelection([this.initialFile]);
    }
  }

  get config(): FileUploadConfig {
    const baseConfig = this.typeConfigurations[this.type];
    return this.customConfig ? { ...baseConfig, ...this.customConfig } : baseConfig;
  }

  get acceptAttribute(): string {
    return this.config.acceptAttribute;
  }

  get buttonText(): string {
    return this.selectedFile ? this.config.changeButtonText : this.config.selectButtonText;
  }

  get hasFile(): boolean {
    return !!this.selectedFile || this.selectedFiles.length > 0;
  }

  get fileInfo(): string {
    if (!this.selectedFile) return '';
    const sizeMB = (this.selectedFile.size / (1024 * 1024)).toFixed(2);
    return `${this.selectedFile.name} (${sizeMB} MB)`;
  }

  get fileIconSvg(): any {
    if (!this.selectedFile) return this.fileIcon;

    const fileType = this.selectedFile.type;
    if (fileType.startsWith('image/')) return this.imageIcon;
    if (fileType === MIME_TYPES.APPLICATION_PDF) return this.pdfIcon;
    if (fileType.includes('excel') || fileType.includes('spreadsheet') || fileType.includes('csv'))
      return this.excelIcon;
    return this.fileIcon;
  }

  // Implement writeValue
  writeValue(value: any): void {
    if (value) {
      if (value instanceof File) {
        this.selectedFile = value;
        // Don't call handleFileSelection here - it triggers validation
        // Just set the file directly
        if (value.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = (e) => {
            this.preview = e.target?.result as string;
            this.cdr.markForCheck();
          };
          reader.readAsDataURL(value);
        }
      } else if (typeof value === 'object' && value.file instanceof File) {
        this.selectedFile = value.file;
        this.preview = value.preview ?? null;
      } else if (typeof value === 'object' && value.isValid !== undefined) {
        // FileUploadResult object
        this.selectedFile = value.file;
        this.preview = value.preview ?? null;
        this.errorMessage = value.error ?? '';
      }
    } else if (this.selectedFile || this.selectedFiles.length > 0) {
      this.selectedFile = null;
      this.selectedFiles = [];
      this.preview = null;
      this.errorMessage = '';
    }
    this.cdr.markForCheck();
  }

  // Implement registerOnChange
  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  // Implement registerOnTouched
  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  // Implement setDisabledState
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  onFileSelectClick(): void {
    if (!this.disabled) {
      this.fileInput.nativeElement.click();
    }
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const files = Array.from(input.files);
      this.handleFileSelection(files);
      this.cdr.detectChanges();
    }
  }

  onDragOver(event: DragEvent): void {
    if (!this.allowDragDrop || this.disabled) return;

    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
    this.cdr.markForCheck();
  }

  onDragLeave(event: DragEvent): void {
    if (!this.allowDragDrop || this.disabled) return;

    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    this.cdr.markForCheck();
  }

  onDrop(event: DragEvent): void {
    if (!this.allowDragDrop || this.disabled) return;

    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const filesArray = Array.from(files);
      this.handleFileSelection(filesArray);
    }
    this.cdr.detectChanges();
  }

  private async handleFileSelection(files: File[]): Promise<void> {
    this.errorMessage = '';
    this.touched = true;

    if (this.multiple) {
      const results: FileUploadResult[] = [];
      for (const file of files) {
        const result = await this.processFile(file);
        if (result.isValid) {
          this.selectedFiles.push(file);
          results.push(result);
        }
      }
      this.filesChange.emit(results);
      this.onChangeCallback(results); // Notify form control
    } else {
      const file = files[0];
      const result = await this.processFile(file);

      if (result.isValid) {
        this.selectedFile = file;
        this.fileSelectEvent.emit(result);
        this.onChangeCallback(result); // Notify form control

        if ((this.type === 'image' || file.type.startsWith('image/')) && this.config.showPreview) {
          this.preview = result.preview ?? null;
        }
      } else {
        this.errorMessage = result.error ?? '';
        this.validationError.emit(this.errorMessage);
      }
    }

    this.onTouchedCallback();
    this.cdr.detectChanges();
  }

  private async processFile(file: File): Promise<FileUploadResult> {
    const validation = await this.validateFile(file);

    if (!validation.isValid) {
      return {
        file: null,
        error: validation.error,
        isValid: false,
      };
    }

    const result: FileUploadResult = {
      file,
      isValid: true,
    };

    // Generate base64 and preview for images
    if (this.type === 'image' || file.type.startsWith('image/')) {
      try {
        result.base64 = await this.fileToBase64(file);
        result.preview = result.base64;
      } catch (error) {
        console.error('Error converting file to base64:', error);
      }
    }

    return result;
  }

  private async validateFile(file: File): Promise<{ isValid: boolean; error?: string }> {
    // Check file type
    const isValidType =
      this.config.acceptedFileTypes.includes('*/*') ||
      this.config.acceptedFileTypes.some((type) => {
        if (type.endsWith('/*')) {
          return file.type.startsWith(type.slice(0, -2));
        }
        return file.type === type;
      });

    if (!isValidType) {
      return {
        isValid: false,
        error:
          this.config.customErrorMessage ??
          `Invalid file type. Accepted types: ${this.config.acceptedFileTypes.join(', ')}`,
      };
    }

    // Check file size
    const maxSizeBytes = this.config.maxFileSize * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return {
        isValid: false,
        error: `File size exceeds ${this.config.maxFileSize}MB limit`,
      };
    }

    // Validate image dimensions if needed
    if (this.config.validateDimensions && file.type.startsWith('image/')) {
      try {
        const dimensions = await this.getImageDimensions(file);

        if (this.config.minWidth && dimensions.width < this.config.minWidth) {
          return {
            isValid: false,
            error: `Image width must be at least ${this.config.minWidth}px`,
          };
        }
        if (this.config.minHeight && dimensions.height < this.config.minHeight) {
          return {
            isValid: false,
            error: `Image height must be at least ${this.config.minHeight}px`,
          };
        }
        if (this.config.maxWidth && dimensions.width > this.config.maxWidth) {
          return {
            isValid: false,
            error: `Image width must not exceed ${this.config.maxWidth}px`,
          };
        }
        if (this.config.maxHeight && dimensions.height > this.config.maxHeight) {
          return {
            isValid: false,
            error: `Image height must not exceed ${this.config.maxHeight}px`,
          };
        }
      } catch (error) {
        console.error('Error validating image dimensions:', error);
      }
    }

    return { isValid: true };
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  }

  private getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  }

  removeFile(): void {
    this.selectedFile = null;
    this.selectedFiles = [];
    this.preview = null;
    this.errorMessage = '';
    this.touched = true;

    // Reset the input
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }

    this.fileRemove.emit();
    this.onChangeCallback(null); // Notify form of change
    this.onTouchedCallback();
    this.cdr.markForCheck();
  }

  clearAll(): void {
    this.removeFile();
  }

  // Public methods for parent component access
  getFile(): File | null {
    return this.selectedFile;
  }

  getFiles(): File[] {
    return this.selectedFiles;
  }

  isValid(): boolean {
    if (this.required) {
      return this.hasFile && !this.errorMessage;
    }
    return !this.errorMessage;
  }

  reset(): void {
    this.clearAll();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const errors: ValidationErrors = {};

    // Check if required and no file selected
    if (this.required) {
      if (this.multiple) {
        if (this.selectedFiles.length === 0) {
          errors['required'] = true;
        }
      } else if (!this.selectedFile) {
        errors['required'] = true;
      }
    }

    this.errors = Object.keys(errors).length > 0 ? errors : null;
    return this.errors;
  }

  // ADD THESE GETTERS:
  get showErrorState(): boolean {
    if (!this.showErrors) return false;

    const hasErrors = !!(this.errors || this.ngControl?.control?.errors);
    if (!hasErrors) return false;

    const isTouched =
      this.touched || this.ngControl?.control?.touched || this.ngControl?.control?.dirty;

    return !!isTouched;
  }

  get currentErrorMessage(): string {
    if (!this.showErrorState) return '';

    const errors = this.ngControl?.control?.errors || this.errors;
    if (!errors) return '';

    if (errors['required']) {
      return 'Please select a file';
    }

    return this.errorMessage || 'Invalid file';
  }

  get combinedErrorMessage(): string {
    // Combine form validation errors with internal validation errors
    if (this.showErrorState && this.currentErrorMessage) {
      return this.currentErrorMessage;
    }

    if (this.errorMessage && this.config.showValidationError) {
      return this.errorMessage;
    }

    return '';
  }
}
